import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import {
  usePanelContext,
  useShouldPanelBeVisible,
} from '~/eds/src/components/Panel/PanelContext';
import { Text } from '~/eds/src/components/Text/Text';

import {
  SelectParentStep,
  SelectParentStepProps,
} from '../AddItemsButton/SelectParentStep';

const messages = defineMessages({
  panelTitle: {
    id: 'MoveToParentPanel.panelTitle',
    defaultMessage: 'Move items',
    description: 'Title for the move items panel',
  },
  description: {
    id: 'MoveToParentPanel.description',
    defaultMessage: 'Select a new parent for the selected items.',
    description: 'Description for the move items panel',
  },
  cancel: {
    id: 'MoveToParentPanel.cancel',
    defaultMessage: 'Cancel',
    description: 'Cancel button text',
  },
  move: {
    id: 'MoveToParentPanel.move',
    defaultMessage: 'Move',
    description: 'Move button text',
  },
});

export const MoveToParentPanelId = 'move-to-parent';

export interface MoveToParentPanelProps {
  selectedIds: string[];
  onClose?: () => void;
  onMove: (nodeIds: string[], newParentNodeId: string) => void;
  onLoadAllNodes?: SelectParentStepProps['onLoadAllNodes'];
  getDescendants: (nodeIds: string[]) => string[];
}

export const MoveToParentPanel = ({
  selectedIds,
  onClose,
  onMove,
  onLoadAllNodes,
  getDescendants,
}: MoveToParentPanelProps) => {
  const { formatMessage } = useIntl();
  const [selectedParentNode, setSelectedParentNode] = useState<string | null>(
    null,
  );

  const { closePanel } = usePanelContext();

  const handleClose = () => {
    setSelectedParentNode(null);
    closePanel(MoveToParentPanelId);
    onClose?.();
  };

  const handleMove = () => {
    if (!selectedParentNode) {
      return;
    }
    onMove(selectedIds, selectedParentNode);
    handleClose();
  };

  const show = useShouldPanelBeVisible(MoveToParentPanelId);

  if (!show) {
    return null;
  }

  return (
    <Panel
      animate
      mode="floating"
      position="right"
      title={formatMessage(messages.panelTitle)}
      width="m"
      hidden={{
        isHidden: false,
        onHide: handleClose,
      }}
      footer={{
        children: (
          <Layout justify="flex-end" spacing={2}>
            <Button
              text={formatMessage(messages.cancel)}
              variant="tertiary"
              onClick={handleClose}
            />
            <Button
              text={formatMessage(messages.move)}
              variant="primary"
              disabled={!selectedParentNode}
              onClick={handleMove}
            />
          </Layout>
        ),
      }}
    >
      <Layout direction="column" spacing={4}>
        <Text color="text.secondary">
          {formatMessage(messages.description)}
        </Text>
        <SelectParentStep
          isVisible
          showDisabledNodesInfo
          selectionRequired
          selectedIds={selectedIds}
          selectedParentNode={selectedParentNode}
          onSelectionChange={setSelectedParentNode}
          onLoadAllNodes={onLoadAllNodes}
          disableNodeIds={getDescendants(selectedIds)}
        />
      </Layout>
    </Panel>
  );
};
